import React from "react"
import {
  Box,
  Flex,
  Image,
  Stack,
  Text,
} from "@chakra-ui/react"
import Icon from "components/elements/Icon"
import {
  GOOGLE_PASS_FONT_FAMILY,
  GOOGLE_PASS_PREVIEW_WIDTH,
  type GoogleWalletPassPreviewProps,
} from "./GoogleWalletPassPreview"
import {
  GOOGLE_BACK_SECTION_LINK_TYPES,
  PASS_BACK_SECTION_TYPE_ICONS,
  PassBackSectionType,
} from "./walletPassBuilderTypes"

type BackSection = {
  label: string
  value: string
  type?: string
}

type Props = GoogleWalletPassPreviewProps & {
  sections?: BackSection[]
  /** Auxiliary builder fields — shown as text modules in the pass details. */
  auxiliaryFields?: BackSection[]
}

const PAGE_BACKGROUND = "#EEF0F8"
const TEXT_PRIMARY = "#202124"
const TEXT_SECONDARY = "#3C4043"
const DIRECTIONS_COLOR = "#1A73E8"
const ROW_GAP = "2px"
const GROUP_GAP = "14px"
const GROUP_RADIUS = "18px"
const ROW_PADDING_X = "20px"
const ROW_PADDING_Y = "16px"
const LABEL_FONT_SIZE = "16px"
const LABEL_LINE_HEIGHT = "22px"
const VALUE_FONT_SIZE = "14px"
const VALUE_LINE_HEIGHT = "20px"
const VALUE_GAP = "2px"
const LINK_ROW_HEIGHT = "56px"
const LINK_ICON_GAP = "16px"
// Logo and event name sit on the page background above the cards, indented to
// line up with the row text inside them.
const HEADER_LOGO_SIZE = "36px"
const HEADER_TITLE_FONT_SIZE = "22px"
const HEADER_TITLE_LINE_HEIGHT = "28px"
const HEADER_PADDING_X = ROW_PADDING_X
const HEADER_PADDING_TOP = "6px"
const HEADER_GAP = "10px"

type DetailRow = {
  label: string
  value: string
  rightIcon?: string
  rightIconColor?: string
}

/** A group of rows rendered as one rounded card with hairline gaps between
 *  rows — matching the Google Wallet details screen. */
const RowGroup = ({ children }: { children: React.ReactNode }) => (
  <Stack spacing={ROW_GAP} borderRadius={GROUP_RADIUS} overflow="hidden">
    {children}
  </Stack>
)

const GoogleWalletPassBackPreview = ({
  sections = [],
  auxiliaryFields = [],
  ...frontProps
}: Props) => {
  const {
    holderName, venueText, dateText, timeText, eventName, logoImageUrl,
  } = frontProps

  const dateTimeText = [dateText, timeText].filter(Boolean).join(", ")
  const textSections = sections.filter(
    (section) => !section.type || !GOOGLE_BACK_SECTION_LINK_TYPES.has(section.type),
  )
  const linkSections = sections.filter(
    (section) => section.type && GOOGLE_BACK_SECTION_LINK_TYPES.has(section.type),
  )

  const detailRows: DetailRow[] = [
    ...(holderName ? [{ label: "Ticket Holder", value: holderName }] : []),
    ...(venueText ? [{
      label: venueText, value: "—", rightIcon: "event-where", rightIconColor: DIRECTIONS_COLOR,
    }] : []),
    ...(dateTimeText ? [{ label: "Event Start Time", value: dateTimeText }] : []),
    ...auxiliaryFields.map((f) => ({ label: f.label, value: f.value })),
    ...textSections.map((s) => ({ label: s.label, value: s.value })),
  ].filter((row) => row.label || row.value)

  return (
    <Box
      w={`${GOOGLE_PASS_PREVIEW_WIDTH}px`}
      bg={PAGE_BACKGROUND}
      borderRadius={GROUP_RADIUS}
      fontFamily={GOOGLE_PASS_FONT_FAMILY}
      p="10px"
    >
      <Stack spacing={GROUP_GAP}>
        {(logoImageUrl || eventName) && (
          <Stack spacing={HEADER_GAP} px={HEADER_PADDING_X} pt={HEADER_PADDING_TOP}>
            {logoImageUrl && (
              <Box w={HEADER_LOGO_SIZE} h={HEADER_LOGO_SIZE} borderRadius="full" overflow="hidden">
                <Image
                  src={logoImageUrl}
                  alt="Pass logo"
                  w="100%"
                  h="100%"
                  objectFit="cover"
                  draggable={false}
                />
              </Box>
            )}
            {eventName && (
              <Text
                fontSize={HEADER_TITLE_FONT_SIZE}
                lineHeight={HEADER_TITLE_LINE_HEIGHT}
                color={TEXT_PRIMARY}
                noOfLines={2}
              >
                {eventName}
              </Text>
            )}
          </Stack>
        )}

        {detailRows.length > 0 && (
          <RowGroup>
            {detailRows.map((row, index) => (
              <Flex key={index} bg="white" px={ROW_PADDING_X} py={ROW_PADDING_Y} align="center" gap={3}>
                <Box flex={1} minW={0}>
                  <Text
                    fontSize={LABEL_FONT_SIZE}
                    lineHeight={LABEL_LINE_HEIGHT}
                    fontWeight={700}
                    color={TEXT_PRIMARY}
                    noOfLines={2}
                  >
                    {row.label}
                  </Text>
                  {row.value && (
                    <Text
                      mt={VALUE_GAP}
                      fontSize={VALUE_FONT_SIZE}
                      lineHeight={VALUE_LINE_HEIGHT}
                      color={TEXT_SECONDARY}
                      noOfLines={3}
                    >
                      {row.value}
                    </Text>
                  )}
                </Box>
                {row.rightIcon && (
                  <Icon icon={row.rightIcon} size={5} color={row.rightIconColor} flexShrink={0} />
                )}
              </Flex>
            ))}
          </RowGroup>
        )}

        {linkSections.length > 0 && (
          <RowGroup>
            {linkSections.map((section, index) => (
              <Flex
                key={index}
                bg="white"
                h={LINK_ROW_HEIGHT}
                px={ROW_PADDING_X}
                align="center"
                gap={LINK_ICON_GAP}
              >
                <Icon
                  icon={PASS_BACK_SECTION_TYPE_ICONS[section.type as PassBackSectionType] || "website"}
                  size={5}
                  color={TEXT_PRIMARY}
                  flexShrink={0}
                />
                <Text
                  fontSize={LABEL_FONT_SIZE}
                  lineHeight={LABEL_LINE_HEIGHT}
                  fontWeight={600}
                  color={TEXT_PRIMARY}
                  isTruncated
                >
                  {section.label || section.value}
                </Text>
              </Flex>
            ))}
          </RowGroup>
        )}
      </Stack>
    </Box>
  )
}

export default GoogleWalletPassBackPreview
